/**
 * 1-Stream B2C Player API client + adapter (v1.3.0 Quick Connect).
 *
 * The B2C API (docs:
 *   https://billing.1-stream.com/whmcs/index.php/knowledgebase/25/B2C-Player-API.html)
 * is similar in spirit to Xtream Codes but pagination-first, UUID-keyed,
 * and returns pre-built play URLs so the client never has to construct
 * `{user}:{pass}@host/movie/{id}.{ext}` style links.
 *
 * Connection encoding — to avoid a backend schema change in v1.3.0 we
 * store the whole connection in the existing `m3u_url` field using a
 * custom scheme:
 *
 *     b2c://USERNAME:PASSWORD@HOST[:PORT][/PATH]
 *
 * `playlistCache.loadPlaylist` sniffs the leading `b2c://` and routes
 * here instead of running the M3U parser.
 *
 * The shipped catalog is normalised into the SAME channel shape used by
 * the rest of the player (Browse, LiveTvWatch, Series, etc.) so the
 * downstream UI is untouched.
 */

// Bump whenever the B2C adapter shape changes in a way that requires a
// re-fetch. Old cached catalogs with a lower (or missing) version are
// invalidated by `playlistCache.loadPlaylist` before serving — keeps
// users from sitting on stale, broken data after an upgrade.
export const B2C_SCHEMA_VERSION = 3;

// ---------- credential parsing ----------------------------------------

const SCHEME = "b2c://";

/**
 * Parse `b2c://user:pass@host[:port]/path` into a plain object.
 * Returns null when the input isn't a B2C URL — callers use this as a
 * cheap sniff to decide which loader to invoke.
 */
export function parseB2cCreds(input) {
  if (typeof input !== "string") return null;
  if (!input.toLowerCase().startsWith(SCHEME)) return null;
  // We can't hand the raw string to `new URL()` because some hosts and
  // some users embed `@` or `:` in passwords. Be lenient: split on the
  // last `@` between scheme and the first `/`.
  const after = input.slice(SCHEME.length);
  const slashIdx = after.indexOf("/");
  const authority = slashIdx === -1 ? after : after.slice(0, slashIdx);
  const path = slashIdx === -1 ? "" : after.slice(slashIdx);
  const atIdx = authority.lastIndexOf("@");
  if (atIdx === -1) return null;
  const userinfo = authority.slice(0, atIdx);
  const hostport = authority.slice(atIdx + 1);
  const colonIdx = userinfo.indexOf(":");
  if (colonIdx === -1) return null;
  const username = decodeURIComponent(userinfo.slice(0, colonIdx));
  const password = decodeURIComponent(userinfo.slice(colonIdx + 1));
  if (!username || !password || !hostport) return null;
  // Default to http if the user pasted a bare host. Most B2C providers
  // run TLS — they should paste `b2c://user:pass@https://host` if they
  // need https, but to keep the URL clean we also support a leading
  // `https-` prefix.
  let scheme = "http";
  let h = hostport;
  if (h.toLowerCase().startsWith("https-")) {
    scheme = "https";
    h = h.slice(6);
  }
  const base = `${scheme}://${h}${path}`.replace(/\/+$/, "");
  return { base, username, password };
}

export function buildB2cUrl({ scheme = "http", host, port, username, password }) {
  const hostport = port ? `${host}:${port}` : host;
  const prefix = scheme === "https" ? "https-" : "";
  const u = encodeURIComponent(username);
  const p = encodeURIComponent(password);
  return `${SCHEME}${u}:${p}@${prefix}${hostport}`;
}

// ---------- low-level HTTP --------------------------------------------
import { webFetch } from "./webFetch";

async function b2cFetch(creds, method, path, { token, body, params } = {}) {
  const url = new URL(`${creds.base}/play/b2c/v1${path}`);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  const headers = { "Accept": "application/json" };
  if (token) headers["Authorization"] = token;
  const init = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  // v1.4.8 — Web Player CORS fix (routes through backend proxy in browser).
  const res = await webFetch(url.toString(), init);
  const text = await res.text();
  let json;
  try { json = text ? JSON.parse(text) : null; } catch { json = null; }
  if (!res.ok || (json && json.errors)) {
    const msg = json?.errors?.message || `${res.status} ${res.statusText}`;
    const err = new Error(`B2C ${path}: ${msg}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

// ---------- auth -------------------------------------------------------

export async function authenticate(creds) {
  const res = await b2cFetch(creds, "POST", "/auth", {
    body: { username: creds.username, password: creds.password },
  });
  if (!res?.auth_token) throw new Error("B2C auth: missing auth_token in response");
  return { token: res.auth_token, expiresAt: res.exp || null };
}

export async function getUserInfo(creds, token) {
  return b2cFetch(creds, "GET", "/user-info", { token });
}

// ---------- categories + listings -------------------------------------

async function getCategories(creds, token, kind /* live|vod|series */) {
  const res = await b2cFetch(creds, "GET", `/categories/${kind}`, { token });
  const map = new Map();
  for (const c of res?.content || []) {
    map.set(String(c.category_id), c.category_name || "");
  }
  return map; // category_id -> category_name
}

/**
 * Pull every page of /content/{kind} and concat. The B2C API caps
 * `per_page` at 1000 in the docs examples — use that so even 10k+
 * catalogs land in ~10 round-trips.
 */
async function getAllContent(creds, token, kind, onProgress) {
  const PER_PAGE = 1000;
  const all = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const res = await b2cFetch(creds, "GET", `/content/${kind}`, {
      token,
      params: { page, per_page: PER_PAGE },
    });
    const items = res?.content || [];
    all.push(...items);
    totalPages = res?.pagination?.total_pages || 1;
    if (typeof onProgress === "function") {
      onProgress({ kind, page, totalPages, fetched: all.length });
    }
    page++;
    // Safety brake — should never hit but prevents an infinite loop if
    // the server lies about pagination.
    if (page > 500) break;
  }
  return all;
}

// ---------- shape adapter ---------------------------------------------

function pickLiveUrl(links) {
  // Prefer m3u8 (HLS) — works on every Android player engine we ship.
  return links?.m3u8 || links?.ts || null;
}
function pickVodUrl(links) {
  return links?.mp4 || links?.mkv || links?.m3u8 || null;
}

function adaptLive(item, catMap, base) {
  const cat = (item.categories || []).map((id) => catMap.get(String(id))).filter(Boolean);
  return {
    id: String(item.stream_id),
    num: item.num ?? null,
    name: item.name || "",
    stream_url: pickLiveUrl(item.links) || "",
    logo_url: item.stream_icon || null,
    group: cat[0] || "Live TV",
    content_type: "live_tv",
    tvg_id: item.epg_channel_id || null,
    epg_channel_id: item.epg_channel_id || null,
    tv_archive: item.tv_archive ? 1 : 0,
    tv_archive_id: item.tv_archive_id || null,
    tv_archive_duration: item.tv_archive_duration || 0,
    is_adult: !!item.is_adult,
    // Mark every B2C row so the catch-up / archive code can build the
    // /play/link_archive URL without needing creds from a parent scope.
    b2c_base: base,
  };
}

function adaptMovie(item, catMap) {
  const cat = (item.categories || []).map((id) => catMap.get(String(id))).filter(Boolean);
  // Year — the listing endpoint doesn't return release_date, only the
  // detail endpoint does. We extract from the name as a cheap proxy so
  // TMDB enrichment + UI year badge still work without an extra round trip.
  let year = null;
  const m = (item.name || "").match(/(?:^|\D)(19\d{2}|20\d{2})(?:\D|$)/);
  if (m) year = parseInt(m[1], 10);
  return {
    id: String(item.stream_id),
    num: item.num ?? null,
    name: item.name || "",
    stream_url: pickVodUrl(item.links) || "",
    logo_url: item.stream_icon || null,
    group: cat[0] || "Movies",
    content_type: "movie",
    rating: item.rating || null,
    rating_5based: item.rating_5based || null,
    added_ts: item.added ? new Date(item.added).getTime() : 0,
    year,
    is_adult: !!item.is_adult,
  };
}

/**
 * Series listing rows are SERIES-LEVEL (one row per show, not per
 * episode). We render them as a single channel row with content_type
 * = "series" and series_id pointing at the B2C UUID. The drill-down
 * view then fetches episodes lazily via `/content/series/{id}`.
 */
function adaptSeries(item, catMap) {
  const cat = (item.categories || []).map((id) => catMap.get(String(id))).filter(Boolean);
  const releaseYear = item.release_date
    ? parseInt(String(item.release_date).slice(0, 4), 10)
    : null;
  return {
    id: `b2c-series-${item.series_id}`,
    num: item.num ?? null,
    name: item.name || "",
    // Series rows don't have a playable URL — Browse won't navigate
    // straight to the player; it opens the series drill-down instead.
    stream_url: "",
    logo_url: item.cover || null,
    group: cat[0] || "Series",
    content_type: "series",
    series_name: item.name || "",
    series_id: item.series_id, // UUID — what /content/series/{id} expects
    xc_kind: "series",
    xc_id: item.series_id,
    year: releaseYear,
    rating: item.rating || null,
    last_episode_added_ts: item.last_modified
      ? new Date(item.last_modified).getTime()
      : 0,
    series_last_modified_ts: item.last_modified
      ? new Date(item.last_modified).getTime()
      : 0,
    // Inline the listing-level info so the series-detail view can render
    // immediately while it lazy-fetches the full episode list.
    _b2c_listing: {
      cover: item.cover,
      genre: item.genre,
      plot: item.plot,
      cast: item.cast,
      director: item.director,
      release_date: item.release_date,
      youtube_trailer: item.youtube_trailer,
      episode_run_time: item.episode_run_time,
      backdrop_path: item.backdrop_path,
    },
  };
}

// ---------- public: fetch the whole catalog ---------------------------

// Simple concurrency-limited Promise.all — used to bound the parallel
// `/content/series/{id}` round-trips while the splash screen is up.
// Sequential would feel slow on big catalogs, unbounded would melt the
// provider. 8 is a sweet spot for HTTP/1.1 keep-alive.
async function mapWithLimit(items, limit, mapper) {
  const out = new Array(items.length);
  let next = 0;
  async function worker() {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      try {
        out[i] = await mapper(items[i], i);
      } catch (e) {
        out[i] = { __error: e?.message || String(e) };
      }
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, worker);
  await Promise.all(workers);
  return out;
}

/**
 * One-shot fetch of the entire B2C catalog. Returns the SAME shape
 * `playlistCache` expects from `parseM3u()` so the rest of the pipeline
 * (chunked IDB write, lazy category load, TMDB enrichment) stays
 * unchanged.
 *
 * `onProgress` is called for each pagination round-trip so the splash
 * screen can show "Fetching live channels 1/4…" style updates.
 */
export async function fetchB2cCatalog(creds, onProgress) {
  const { token } = await authenticate(creds);

  // Categories are tiny and independent — fetch all three in parallel.
  const [liveCats, vodCats, seriesCats] = await Promise.all([
    getCategories(creds, token, "live"),
    getCategories(creds, token, "vod"),
    getCategories(creds, token, "series"),
  ]);

  // Listings are large — fetch sequentially with progress so we don't
  // hammer the provider with three parallel paginated walks.
  const liveItems = await getAllContent(creds, token, "live", onProgress);
  const vodItems = await getAllContent(creds, token, "vod", onProgress);
  const seriesListings = await getAllContent(creds, token, "series", onProgress);

  // Series → ONE row per show (the listing itself), with episodes
  // lazy-fetched when the user actually opens the show. The eager
  // per-series drill we did in 1.2.22-1.2.24 was the bottleneck that
  // took 15+ minutes on big catalogs — concurrency-limited round
  // trips to `/content/series/{id}` for every show on the platform.
  //
  // The new model:
  //   - Catalog import takes ~30-90 seconds (just the listings).
  //   - First time the user opens a series, the drill-down fires the
  //     missing `/content/series/{id}` call (Browse.jsx handles it),
  //     stores the episodes back into IDB, and renders.
  //   - All subsequent opens are instant (episodes cached in IDB).
  //
  // The series LISTING row already carries `cover`, `plot`, `cast`,
  // `genre`, `release_date`, `rating` — enough for the tile poster +
  // synopsis to render before any episodes are loaded.
  const seriesEpisodeRows = seriesListings.map((listing) => {
    const catNames = (listing.categories || [])
      .map((id) => seriesCats.get(String(id)))
      .filter(Boolean);
    const releaseYear = listing.release_date
      ? parseInt(String(listing.release_date).slice(0, 4), 10)
      : null;
    const seriesInfo = {
      cover: listing.cover || null,
      cover_big: listing.cover || null,
      plot: listing.plot || null,
      cast: listing.cast || null,
      director: listing.director || null,
      genre: listing.genre || null,
      releaseDate: listing.release_date || null,
      release_date: listing.release_date || null,
      rating: listing.rating || null,
      youtube_trailer: listing.youtube_trailer || null,
      episode_run_time: listing.episode_run_time || null,
      backdrop_path: listing.backdrop_path || null,
      name: listing.name || null,
      series_id: listing.series_id,
    };
    return {
      // Synthetic "placeholder" episode row that gets the tile to
      // render. `stream_url` is empty on purpose — when the user
      // opens the tile we replace this row with the real episode
      // list. Browse skips this row in the played-episode list when
      // `_b2c_placeholder` is set.
      id: `b2c-series-${listing.series_id}`,
      num: listing.num ?? null,
      name: listing.name || "",
      stream_url: "",
      logo_url: listing.cover || null,
      group: catNames[0] || "Series",
      content_type: "series",
      series_name: listing.name || "",
      series_id: listing.series_id,
      season: null,
      episode: null,
      year: releaseYear,
      xc_kind: "series",
      xc_id: listing.series_id,
      rating: listing.rating || null,
      b2c_series_info: seriesInfo,
      // Marker so the drill-down knows it needs to fetch episodes
      // before showing playable rows.
      _b2c_placeholder: true,
      _b2c_needs_episodes: true,
      series_last_modified_ts: listing.last_modified
        ? new Date(listing.last_modified).getTime()
        : 0,
      last_episode_added_ts: 0,
    };
  });

  const channels = [
    ...liveItems.map((it) => adaptLive(it, liveCats, creds.base)),
    ...vodItems.map((it) => adaptMovie(it, vodCats)),
    ...seriesEpisodeRows,
  ];
  // Stable `order` so the existing Browse sort stays deterministic.
  channels.forEach((c, i) => { c.order = i; });

  return {
    channels,
    // No XMLTV walk on import — Live TV view fetches `/epg/{id}/short`
    // on-demand. This keeps the import lightweight (the user is staring
    // at the splash screen) and avoids parsing megabytes of XML twice.
    epgByChannel: {},
    xtream: null,
    fetched_at: Date.now(),
    source: "b2c",
    b2c_schema_version: B2C_SCHEMA_VERSION,
    // Stash the token + base so on-demand calls (series episodes, EPG,
    // catchup, most_watched) don't need to re-auth. Token is long-lived
    // (the docs example shows `exp` ~2071) so cache is safe.
    b2c: { base: creds.base, token },
  };
}

// ---------- public: lazy series episodes ------------------------------

/**
 * Fetch full episode list for one series. Called when the user opens a
 * series tile in Browse. Returns episodes already adapted to the
 * "channel row" shape so the existing episodes list can render them.
 */
export async function fetchB2cSeriesEpisodes(creds, token, seriesId) {
  const res = await b2cFetch(creds, "GET", `/content/series/${seriesId}`, { token });
  const out = [];
  const info = res?.info || {};
  const seasons = res?.seasons || [];
  // `episodes` in the docs is a 2D array: episodes[seasonIdx] = [..].
  // Some providers actually return an object keyed by season number —
  // handle both.
  const epsContainer = res?.episodes;
  const seasonsArr = Array.isArray(epsContainer)
    ? epsContainer
    : Object.values(epsContainer || {});
  seasonsArr.forEach((seasonEps, seasonIdx) => {
    const seasonNum = seasons[seasonIdx]?.season_number ?? (seasonIdx + 1);
    (seasonEps || []).forEach((ep) => {
      out.push({
        id: `b2c-ep-${ep.id}`,
        name: ep.title || `Episode ${ep.episode_num}`,
        stream_url: ep.links?.mp4 || ep.links?.mkv || ep.links?.m3u8 || "",
        logo_url: info.cover || null,
        group: "Series",
        content_type: "series",
        series_name: info.name || "",
        series_id: seriesId,
        season: ep.season ?? seasonNum,
        episode: ep.episode_num,
        duration: ep.info?.duration || null,
        added_ts: ep.added ? new Date(ep.added).getTime() : 0,
      });
    });
  });
  return { episodes: out, info, seasons };
}

// ---------- public: live EPG (now-playing) ----------------------------

export async function fetchB2cEpgShort(creds, token, streamId) {
  const res = await b2cFetch(creds, "GET", `/epg/${streamId}/short`, { token });
  return (res?.epg_listings || []).map((p) => ({
    title: p.title ? safeAtob(p.title) : "",
    description: p.description ? safeAtob(p.description) : "",
    start: p.start_timestamp ? p.start_timestamp * 1000 : Date.parse(p.start),
    end: p.end_timestamp ? p.end_timestamp * 1000 : Date.parse(p.end),
    has_archive: !!p.has_archive,
    now_playing: !!p.now_playing,
  }));
}

function safeAtob(s) {
  try { return decodeURIComponent(escape(atob(s))); } catch { return s; }
}

// ---------- public: catch-up / archive URL ----------------------------

/**
 * Build a tv_archive URL — used by the Catch-Up screen.
 *
 *   /play/link_archive_nt/{archiveOrStream}/offset_{seconds}.m3u8?token=…
 *
 * `archiveOrStream` should be `tv_archive_id` when present (channel's
 * dedicated archive feed) or `stream_id` (server falls back to the live
 * source).
 */
export function buildB2cArchiveUrl({ base, token }, archiveOrStreamId, offsetSeconds) {
  const u = new URL(
    `${base}/play/link_archive_nt/${archiveOrStreamId}/offset_${offsetSeconds}.m3u8`
  );
  if (token) u.searchParams.set("token", token);
  return u.toString();
}

// ---------- public: trending / most-watched ---------------------------

export async function fetchB2cMostWatched(creds, token, { perPage = 12, page = 1 } = {}) {
  const res = await b2cFetch(creds, "GET", "/statistics/streams/most_watched", {
    token,
    params: { per_page: perPage, page },
  });
  return res?.data || [];
}
